import React, { useCallback, useState } from 'react';
import { uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { Menu } from '../../../ui';
import { MenuButton } from '../../../ui/MenuButton';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { useResolve } from '../../Container';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { ItemPrototype } from './VoterItem';

// Colors from ContrastColorList
const contrastColors = {
    indigo: '#3f51b5',
    cyan: '#00bcd4',
    lightGreen: '#8bc34a',
    amber: '#ffc107',
    red: '#f44336',
    purple: '#9c27b0',
    blue: '#2196f3',
    teal: '#009688',
    lime: '#cddc39',
    orange: '#ff9800',
    deepPurple: '#673ab7',
    lightBlue: '#03a9f4',
    green: '#4caf50',
    yellow: '#ffeb3b',
    deepOrange: '#ff5722',
};

const presets = {
    '1-5': [
        ItemPrototype(uuid(), '1', contrastColors.blue),
        ItemPrototype(uuid(), '2', contrastColors.lightBlue),
        ItemPrototype(uuid(), '3', contrastColors.cyan),
        ItemPrototype(uuid(), '4', contrastColors.teal),
        ItemPrototype(uuid(), '5', contrastColors.indigo),
    ],
    '1-10': [
        ItemPrototype(uuid(), '1', contrastColors.lightBlue),
        ItemPrototype(uuid(), '2', contrastColors.blue),
        ItemPrototype(uuid(), '3', contrastColors.indigo),
        ItemPrototype(uuid(), '4', contrastColors.deepPurple),
        ItemPrototype(uuid(), '5', contrastColors.purple),
        ItemPrototype(uuid(), '6', contrastColors.teal),
        ItemPrototype(uuid(), '7', contrastColors.cyan),
        ItemPrototype(uuid(), '8', contrastColors.lightGreen),
        ItemPrototype(uuid(), '9', contrastColors.lime),
        ItemPrototype(uuid(), '10', contrastColors.amber),
    ],
    'Mood': [
        ItemPrototype(uuid(), '3D', contrastColors.green),
        ItemPrototype(uuid(), ':)', contrastColors.lightGreen),
        ItemPrototype(uuid(), ':|', contrastColors.orange),
        ItemPrototype(uuid(), ':(', contrastColors.deepOrange),
        ItemPrototype(uuid(), '>:(', contrastColors.red),
    ],
    'Modified Fibonacci': [
        ItemPrototype(uuid(), '0', contrastColors.lightBlue),
        ItemPrototype(uuid(), '1', contrastColors.blue),
        ItemPrototype(uuid(), '2', contrastColors.indigo),
        ItemPrototype(uuid(), '3', contrastColors.deepPurple),
        ItemPrototype(uuid(), '5', contrastColors.purple),
        ItemPrototype(uuid(), '8', contrastColors.teal),
        ItemPrototype(uuid(), '13', contrastColors.cyan),
        ItemPrototype(uuid(), '20', contrastColors.lime),
        ItemPrototype(uuid(), '40', contrastColors.amber),
        ItemPrototype(uuid(), '100', contrastColors.red),
    ],
    'T-Shirt Sizes': [
        ItemPrototype(uuid(), 'XS', contrastColors.lightBlue),
        ItemPrototype(uuid(), 'S', contrastColors.cyan),
        ItemPrototype(uuid(), 'M', contrastColors.teal),
        ItemPrototype(uuid(), 'L', contrastColors.indigo),
        ItemPrototype(uuid(), 'XL', contrastColors.deepPurple),
        ItemPrototype(uuid(), 'XXL', contrastColors.purple),
    ],
    'Risk Level': [
        ItemPrototype(uuid(), 'Low', contrastColors.green),
        ItemPrototype(uuid(), 'Medium', contrastColors.amber),
        ItemPrototype(uuid(), 'High', contrastColors.red),
        ItemPrototype(uuid(), 'Very High', contrastColors.purple),
    ],
    'MoSCoW': [
        ItemPrototype(uuid(), 'Must', contrastColors.red),
        ItemPrototype(uuid(), 'Should', contrastColors.orange),
        ItemPrototype(uuid(), 'Could', contrastColors.amber),
        ItemPrototype(uuid(), 'Won\'t', contrastColors.blue),
    ],
    'Yes/No/Maybe': [
        ItemPrototype(uuid(), 'Yes', contrastColors.green),
        ItemPrototype(uuid(), 'No', contrastColors.red),
        ItemPrototype(uuid(), 'Maybe', contrastColors.amber),
    ],
    'Yes/No': [
        ItemPrototype(uuid(), '(Y)', contrastColors.green),
        ItemPrototype(uuid(), '(N)', contrastColors.red),
    ],
    'Participants': [],
};

export const VoterItemQuickSetMenu = ({ onSelect }: { onSelect: (items: ItemPrototype[]) => void }) => {

    const ptpts = useResolve(ParticipantManager);

    const onSelectParticipants = () => {
        const items = ptpts.map(x => ItemPrototype(uuid(), `(${x.id})`, x.color));
        onSelect(items);
    };

    const onSelectPreset = (preset: keyof typeof presets) => () => {
        onSelect(presets[preset]);
    };

    return <Flexer>
        <Menu>
            <Menu.Item onClick={onSelectPreset('1-5')}>
                <Menu.ItemText>1 - 5</Menu.ItemText>
            </Menu.Item>
            <Menu.Item onClick={onSelectPreset('1-10')}>
                <Menu.ItemText>1 - 10</Menu.ItemText>
            </Menu.Item>
            <Menu.Item onClick={onSelectPreset('Mood')}>
                <Menu.ItemText>Mood</Menu.ItemText>
            </Menu.Item>
            <Menu.Item onClick={onSelectPreset('Yes/No')}>
                <Menu.ItemText>Thumbs Up / Down</Menu.ItemText>
            </Menu.Item>
            <Menu.Item onClick={onSelectPreset('Yes/No/Maybe')}>
                <Menu.ItemText>Yes / No / Maybe</Menu.ItemText>
            </Menu.Item>
            <Menu.Item onClick={onSelectPreset('Modified Fibonacci')}>
                <Menu.ItemText>Modified Fibonacci</Menu.ItemText>
            </Menu.Item>
            <Menu.Item onClick={onSelectPreset('T-Shirt Sizes')}>
                <Menu.ItemText>T-Shirt Sizes</Menu.ItemText>
            </Menu.Item>
            <Menu.Item onClick={onSelectPreset('Risk Level')}>
                <Menu.ItemText>Risk Level</Menu.ItemText>
            </Menu.Item>
            <Menu.Item onClick={onSelectPreset('MoSCoW')}>
                <Menu.ItemText>MoSCoW</Menu.ItemText>
            </Menu.Item>
            <Menu.Item onClick={onSelectParticipants}>
                <Menu.ItemText>Team Members</Menu.ItemText>
            </Menu.Item>
        </Menu>
    </Flexer>;
};

export const VoterItemQuickSetMenuButton = ({ onSelect }: { onSelect: (items: ItemPrototype[]) => void }) => {

    const [open, setOpen] = useState(false);
    const onSelectProxy = useCallback((items: ItemPrototype[]) => {
        setOpen(false);
        onSelect(items);
    }, [onSelect]);

    return <MenuButton label="Quick Set" size="sm" open={open} setOpen={setOpen} >
        <VoterItemQuickSetMenu onSelect={onSelectProxy} />
    </MenuButton>;
};
